// FeeCalculator.js

import React, { useState } from 'react';
import {
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Slider,
  Paper,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { NumericFormat } from 'react-number-format';
import { v4 as uuidv4 } from 'uuid';
import { motion } from 'framer-motion';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import { Card, CardContent } from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import { Table as AntTable, ConfigProvider } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { blue, lightBlue, blueGrey } from '@mui/material/colors';
import { ColorPicker } from 'antd';

// Theme and styled components
const theme = createTheme({
  palette: {
    primary: {
      main: blue[700],
      light: blue[300],
      dark: blue[900],
    },
    secondary: {
      main: blueGrey[600],
      light: blueGrey[400],
      dark: blueGrey[800],
    },
    background: {
      default: lightBlue[50],
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: "'Poppins', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 15,
          },
        },
      },
    },
  },
});

const GlassCard = muiStyled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  borderRadius: 30,
  boxShadow: `0 8px 32px 0 ${alpha(theme.palette.primary.main, 0.2)}`,
  border: `1px solid ${alpha(theme.palette.primary.light, 0.18)}`,
  padding: theme.spacing(4),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: `0 12px 40px 0 ${alpha(theme.palette.primary.main, 0.25)}`,
  },
}));

const ColorButton = muiStyled(Button)(({ theme, color }) => ({
  color: theme.palette.getContrastText(color),
  backgroundColor: color,
  '&:hover': {
    backgroundColor: alpha(color, 0.9),
  },
  borderRadius: 30,
  padding: '10px 20px',
  fontWeight: 600,
}));

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 10px;
  }
`;

const StyledSlider = styled(Slider)`
  color: ${blue[500]};
`;

const SectionSubtitle = styled(Typography)`
  font-size: 1.2rem;
  color: #4a4a4a;
  margin-bottom: 1rem;
  font-weight: 500;
`;

const StyledTableContainer = styled('div')`
  .ant-table {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

  .ant-table-thead > tr > th {
    background: white !important;
    color: rgb(75, 85, 99) !important;
    font-weight: 500;
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
    
    &:hover {
      background: #fafafa !important;
    }
    
    &.ant-table-column-has-sorters:hover {
      background: #fafafa !important;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-table-tbody > tr:nth-of-type(even) {
    background-color: #fafafa;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f5f5f5 !important;
  }

  @media (max-width: 767px) {
    .ant-table {
      font-size: 0.875rem;
    }
    
    .ant-table-thead > tr > th {
      padding: 8px 4px;
      white-space: nowrap;
    }
    
    .ant-table-tbody > tr > td {
      padding: 8px 4px;
    }

    .ant-space {
      gap: 4px !important;
    }
  }
`;

const FeeCalculator = () => {
  // State variables
  const [scenarios, setScenarios] = useState([]);
  const [currentScenario, setCurrentScenario] = useState({
    id: uuidv4(),
    name: "Scenario 1",
    planAssets: '1000000',
    annualContributions: '50000',
    investmentReturnRate: 8,
    numberOfYears: 10,
    feeStructureType: 'Flat Rate',
    flatFeeRate: 1,
    feeTiers: [
      { threshold: 0, rate: 1 },
      { threshold: 1000000, rate: 0.75 },
      { threshold: 3000000, rate: 0.5 },
    ],
    includeInGraph: true,
    color: scenarios.length === 0 ? '#26cfd9' : `hsl(${Math.random() * 360}, 70%, 50%)`,
  });

  // Helper function to calculate advisor fee
  const calculateAdvisorFee = (assets, scenario) => {
    let fee = 0;
    if (scenario.feeStructureType === 'Flat Rate') {
      fee = (assets * scenario.flatFeeRate) / 100;
    } else if (scenario.feeStructureType === 'Tiered Rate') {
      let remainingAssets = assets;
      const sortedTiers = [...scenario.feeTiers].sort((a, b) => a.threshold - b.threshold);
      for (let i = sortedTiers.length - 1; i >= 0; i--) {
        if (remainingAssets > sortedTiers[i].threshold) {
          fee += ((remainingAssets - sortedTiers[i].threshold) * sortedTiers[i].rate) / 100;
          remainingAssets = sortedTiers[i].threshold;
        }
      }
    }
    return fee;
  };

  // Function to generate data for the scenario
  const generateScenarioData = (scenario) => {
    const years = Array.from({ length: scenario.numberOfYears }, (_, i) => i + 1);
    const data = [];

    let assets = parseFloat(scenario.planAssets) || 0;
    const contributions = parseFloat(scenario.annualContributions) || 0;
    const returnRate = scenario.investmentReturnRate / 100;

    for (let year of years) {
      assets = (assets + contributions) * (1 + returnRate);
      const compensation = calculateAdvisorFee(assets, scenario);
      data.push({
        year: `Year ${year}`,
        assets,
        compensation,
        name: scenario.name,
      });
    }
    return data;
  };

  // Scenario management functions
  const handleEditScenario = (id) => {
    const scenarioToEdit = scenarios.find(scenario => scenario.id === id);
    setCurrentScenario(scenarioToEdit);
  };

  const handleAddOrUpdateScenario = () => {
    const existingScenarioIndex = scenarios.findIndex(s => s.id === currentScenario.id);
    
    if (existingScenarioIndex !== -1) {
      const updatedScenarios = [...scenarios];
      updatedScenarios[existingScenarioIndex] = { ...currentScenario };
      setScenarios(updatedScenarios);
    } else {
      const newScenarioName = `Scenario ${scenarios.length + 1}`;
      setScenarios([...scenarios, { ...currentScenario, name: newScenarioName }]);
    }

    setCurrentScenario({
      id: uuidv4(),
      name: `Scenario ${scenarios.length + 2}`,
      planAssets: '1000000',
      annualContributions: '50000',
      investmentReturnRate: 8,
      numberOfYears: 10,
      feeStructureType: 'Flat Rate',
      flatFeeRate: 1,
      feeTiers: [
        { threshold: 0, rate: 1 },
        { threshold: 1000000, rate: 0.75 },
        { threshold: 3000000, rate: 0.5 },
      ],
      includeInGraph: true,
      color: `hsl(${Math.random() * 360}, 70%, 50%)`,
    });
  };

  const handleRemoveScenario = (id) => {
    const updatedScenarios = scenarios.filter((scenario) => scenario.id !== id);
    const renamedScenarios = updatedScenarios.map((scenario, index) => ({
      ...scenario,
      name: `Scenario ${index + 1}`
    }));
    setScenarios(renamedScenarios);
    
    setCurrentScenario(prev => ({
      ...prev,
      name: `Scenario ${renamedScenarios.length + 1}`
    }));
  };

  // Prepare chart data
  const prepareChartData = () => {
    let combinedData = [];
    const scenariosToGraph = scenarios.length > 0 ? scenarios : [currentScenario];
    
    for (let year = 1; year <= Math.max(...scenariosToGraph.map(s => s.numberOfYears)); year++) {
      combinedData.push({ year: `Year ${year}` });
    }
    
    scenariosToGraph.forEach((scenario) => {
      if (scenario.includeInGraph) {
        const scenarioData = generateScenarioData(scenario);
        scenarioData.forEach((dataPoint, index) => {
          combinedData[index] = {
            ...combinedData[index],
            [`${scenario.name} Assets`]: parseFloat(dataPoint.assets) || 0,
            [`${scenario.name} Compensation`]: parseFloat(dataPoint.compensation) || 0,
            [`${scenario.name} Basis Points`]: calculateBasisPoints(parseFloat(dataPoint.assets) || 0, parseFloat(dataPoint.compensation) || 0),
          };
        });
      }
    });
    
    return combinedData;
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-300 p-2 rounded">
          <p className="label">{label}</p>
          {payload.map((entry, index) => (
            <p key={`item-${index}`}>{`${entry.name}: $${entry.value.toLocaleString()}`}</p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Format numbers with commas
  const formatNumber = (value) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Calculate basis points
  const calculateBasisPoints = (assets, compensation) => {
    if (assets === 0) return 0;
    return ((compensation / assets) * 10000);
  };

  // Handle color change
  const handleColorChange = (color) => {
    setCurrentScenario({ ...currentScenario, color: color.hex });
  };

  // Download Excel
  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(prepareChartData());
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Advisor Fee Data");
    XLSX.writeFile(workbook, "advisor_fee_data.xlsx");
  };

  // Table state and handling
  const [sortedInfo, setSortedInfo] = useState({});
  const handleTableChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
  };

  // Create columns for the table
  const columns = [
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      sorter: (a, b) => a.year.localeCompare(b.year),
      sortOrder: sortedInfo.columnKey === 'year' && sortedInfo.order,
    },
    ...(scenarios.length > 0 ? scenarios : [currentScenario]).flatMap((scenario) => 
      scenario.includeInGraph ? [
        {
          title: `${scenario.name} Assets`,
          dataIndex: `${scenario.name} Assets`,
          key: `${scenario.name} Assets`,
          sorter: (a, b) => (a[`${scenario.name} Assets`] || 0) - (b[`${scenario.name} Assets`] || 0),
          sortOrder: sortedInfo.columnKey === `${scenario.name} Assets` && sortedInfo.order,
          render: (value) => (typeof value === 'number' ? `$${formatNumber(value.toFixed(2))}` : 'N/A'),
        },
        {
          title: `${scenario.name} Compensation`,
          dataIndex: `${scenario.name} Compensation`,
          key: `${scenario.name} Compensation`,
          sorter: (a, b) => (a[`${scenario.name} Compensation`] || 0) - (b[`${scenario.name} Compensation`] || 0),
          sortOrder: sortedInfo.columnKey === `${scenario.name} Compensation` && sortedInfo.order,
          render: (value) => (typeof value === 'number' ? `$${formatNumber(value.toFixed(2))}` : 'N/A'),
        },
        {
          title: `${scenario.name} Basis Points`,
          dataIndex: `${scenario.name} Basis Points`,
          key: `${scenario.name} Basis Points`,
          sorter: (a, b) => (a[`${scenario.name} Basis Points`] || 0) - (b[`${scenario.name} Basis Points`] || 0),
          sortOrder: sortedInfo.columnKey === `${scenario.name} Basis Points` && sortedInfo.order,
          render: (value) => (typeof value === 'number' ? `${value.toFixed(2)} bps` : 'N/A'),
        },
      ] : []
    ),
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        style={{ padding: '24px' }}
      >
        <Grid container spacing={4}>
          {/* Input Section */}
          <Grid item xs={12} md={6}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Define Scenario
                </SectionSubtitle>
                <NumericFormat
                  customInput={StyledTextField}
                  label="Initial Plan Assets"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={currentScenario.planAssets}
                  onValueChange={(values) =>
                    setCurrentScenario({ ...currentScenario, planAssets: values.value })
                  }
                  thousandSeparator={true}
                  prefix={'$'}
                />
                <NumericFormat
                  customInput={StyledTextField}
                  label="Annual Plan Contributions"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={currentScenario.annualContributions}
                  onValueChange={(values) =>
                    setCurrentScenario({
                      ...currentScenario,
                      annualContributions: values.value,
                    })
                  }
                  thousandSeparator={true}
                  prefix={'$'}
                />
                <Typography gutterBottom>
                  Investment Return Rate: {currentScenario.investmentReturnRate}%
                </Typography>
                <StyledSlider
                  value={currentScenario.investmentReturnRate}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, investmentReturnRate: val })
                  }
                  aria-labelledby="investment-return-rate"
                  valueLabelDisplay="auto"
                  step={0.5}
                  min={0}
                  max={15}
                />
                <Typography gutterBottom>
                  Number of Years: {currentScenario.numberOfYears}
                </Typography>
                <StyledSlider
                  value={currentScenario.numberOfYears}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, numberOfYears: val })
                  }
                  aria-labelledby="number-of-years"
                  valueLabelDisplay="auto"
                  step={1}
                  min={1}
                  max={30}
                />
                <FormControl variant="outlined" fullWidth margin="normal">
                  <InputLabel>Fee Structure Type</InputLabel>
                  <Select
                    value={currentScenario.feeStructureType}
                    onChange={(e) =>
                      setCurrentScenario({ ...currentScenario, feeStructureType: e.target.value })
                    }
                    label="Fee Structure Type"
                  >
                    <MenuItem value="Flat Rate">Flat Rate</MenuItem>
                    <MenuItem value="Tiered Rate">Tiered Rate</MenuItem>
                  </Select>
                </FormControl>
                {currentScenario.feeStructureType === 'Flat Rate' && (
                  <StyledTextField
                    label="Flat Fee Rate (%)"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    type="number"
                    value={currentScenario.flatFeeRate}
                    onChange={(e) =>
                      setCurrentScenario({ ...currentScenario, flatFeeRate: e.target.value })
                    }
                  />
                )}
                {currentScenario.feeStructureType === 'Tiered Rate' && (
                  <>
                    {currentScenario.feeTiers.map((tier, index) => (
                      <Grid container spacing={2} key={index}>
                        <Grid item xs={5}>
                          <StyledTextField
                            label="Threshold ($)"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            type="number"
                            value={tier.threshold}
                            onChange={(e) => {
                              const newTiers = [...currentScenario.feeTiers];
                              newTiers[index].threshold = e.target.value;
                              setCurrentScenario({ ...currentScenario, feeTiers: newTiers });
                            }}
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <StyledTextField
                            label="Rate (%)"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            type="number"
                            value={tier.rate}
                            onChange={(e) => {
                              const newTiers = [...currentScenario.feeTiers];
                              newTiers[index].rate = e.target.value;
                              setCurrentScenario({ ...currentScenario, feeTiers: newTiers });
                            }}
                          />
                        </Grid>
                        <Grid item xs={2} style={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton
                            color="secondary"
                            onClick={() => {
                              const newTiers = [...currentScenario.feeTiers];
                              newTiers.splice(index, 1);
                              setCurrentScenario({ ...currentScenario, feeTiers: newTiers });
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newTiers = [...currentScenario.feeTiers, { threshold: '', rate: '' }];
                        setCurrentScenario({ ...currentScenario, feeTiers: newTiers });
                      }}
                    >
                      Add Tier
                    </Button>
                  </>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={currentScenario.includeInGraph}
                        onChange={(e) =>
                          setCurrentScenario({ ...currentScenario, includeInGraph: e.target.checked })
                        }
                        name="includeInGraph"
                        color="primary"
                      />
                    }
                    label="Include in Graph"
                  />
                  <ColorPicker
                    value={currentScenario.color}
                    onChange={(color) => handleColorChange({ hex: color.toHexString() })}
                    trigger="hover"
                    disabledAlpha
                    presets={[
                      {
                        label: 'Recommended',
                        colors: [
                          '#4a90e2', '#50C878', '#FF6B6B', '#9B59B6',
                          '#34495E', '#2ECC71', '#E74C3C', '#8E44AD'
                        ],
                      },
                    ]}
                  />
                </Box>
                <Box sx={{ mt: 3 }}>
                  <ColorButton
                    variant="contained"
                    onClick={handleAddOrUpdateScenario}
                    startIcon={<AddIcon />}
                    color={currentScenario.color}
                    fullWidth
                  >
                    {scenarios.some(s => s.id === currentScenario.id) ? 'Update Scenario' : 'Add Scenario'}
                  </ColorButton>
                </Box>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Scenario List */}
          <Grid item xs={12} md={6}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Scenarios
                </SectionSubtitle>
                {scenarios.length > 0 ? (
                  scenarios.map((scenario) => (
                    <Box key={scenario.id} sx={{ mb: 2, position: 'relative' }}>
                      <Paper
                        elevation={3}
                        sx={{
                          p: 2,
                          borderRadius: '10px',
                          background: alpha(scenario.color, 0.1),
                          border: `1px solid ${alpha(scenario.color, 0.3)}`,
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: scenario.color }}>
                          {scenario.name}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={scenario.includeInGraph}
                                onChange={(e) => {
                                  const newScenarios = scenarios.map((s) =>
                                    s.id === scenario.id ? { ...s, includeInGraph: e.target.checked } : s
                                  );
                                  setScenarios(newScenarios);
                                }}
                                name="includeInGraph"
                                color="primary"
                              />
                            }
                            label="Include in Graph"
                          />
                          <Box>
                            <IconButton
                              color="primary"
                              onClick={() => handleEditScenario(scenario.id)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="secondary"
                              onClick={() => handleRemoveScenario(scenario.id)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body1">No saved scenarios. Create one to get started!</Typography>
                )}
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Plan Assets Over Time Chart */}
          <Grid item xs={12}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Plan Assets Over Time
                </SectionSubtitle>
                <ResponsiveContainer width="100%" height={500}>
                  <AreaChart data={prepareChartData()}>
                    <defs>
                      {scenarios.map((scenario) => (
                        <linearGradient key={scenario.id} id={`color${scenario.id}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={scenario.color} stopOpacity={0.8}/>
                          <stop offset="95%" stopColor={scenario.color} stopOpacity={0.1}/>
                        </linearGradient>
                      ))}
                    </defs>
                    <XAxis dataKey="year" />
                    <YAxis />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend />
                    {scenarios.map((scenario) => {
                      if (scenario.includeInGraph) {
                        return (
                          <Area
                            key={scenario.id}
                            type="monotone"
                            dataKey={`${scenario.name} Assets`}
                            name={`${scenario.name} Assets`}
                            stroke={scenario.color}
                            fillOpacity={1}
                            fill={`url(#color${scenario.id})`}
                          />
                        );
                      }
                      return null;
                    })}
                    {scenarios.length === 0 && (
                      <Area
                        type="monotone"
                        dataKey={`${currentScenario.name} Assets`}
                        name={`${currentScenario.name} Assets`}
                        stroke={currentScenario.color}
                        fill={currentScenario.color}
                        fillOpacity={0.3}
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Compensation Over Time Chart */}
          <Grid item xs={12}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Compensation Over Time
                </SectionSubtitle>
                <ResponsiveContainer width="100%" height={500}>
                  <AreaChart data={prepareChartData()}>
                    <defs>
                      {scenarios.map((scenario) => (
                        <linearGradient key={`comp-${scenario.id}`} id={`comp-color${scenario.id}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={scenario.color} stopOpacity={0.8}/>
                          <stop offset="95%" stopColor={scenario.color} stopOpacity={0.1}/>
                        </linearGradient>
                      ))}
                    </defs>
                    <XAxis dataKey="year" />
                    <YAxis />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend />
                    {scenarios.map((scenario) => {
                      if (scenario.includeInGraph) {
                        return (
                          <Area
                            key={`comp-${scenario.id}`}
                            type="monotone"
                            dataKey={`${scenario.name} Compensation`}
                            name={`${scenario.name} Compensation`}
                            stroke={scenario.color}
                            fillOpacity={1}
                            fill={`url(#comp-color${scenario.id})`}
                          />
                        );
                      }
                      return null;
                    })}
                    {scenarios.length === 0 && (
                      <Area
                        type="monotone"
                        dataKey={`${currentScenario.name} Compensation`}
                        name={`${currentScenario.name} Compensation`}
                        stroke={currentScenario.color}
                        fill={currentScenario.color}
                        fillOpacity={0.3}
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Data Table */}
          <Grid item xs={12}>
            <GlassCard variants={itemVariants} style={{ marginBottom: '2rem' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <SectionSubtitle variant="h6" sx={{ color: theme.palette.secondary.main }}>
                    Detailed Data
                  </SectionSubtitle>
                  <Tooltip title="Download Excel">
                    <Button
                      onClick={downloadExcel}
                      startIcon={<DownloadOutlined />}
                      sx={{
                        color: theme.palette.secondary.main,
                        '&:hover': {
                          color: theme.palette.primary.main,
                          backgroundColor: 'transparent',
                        },
                        transition: 'all 0.3s ease',
                        textTransform: 'none',
                        fontSize: '14px',
                      }}
                    >
                      Download Excel
                    </Button>
                  </Tooltip>
                </Box>
                <ConfigProvider
                  theme={{
                    token: {
                      fontFamily: "'Poppins', sans-serif",
                      borderRadius: 12,
                      colorBgContainer: '#ffffff',
                      colorBorderSecondary: '#f0f0f0',
                      fontSize: 14,
                    },
                    components: {
                      Table: {
                        headerBg: '#ffffff',
                        headerColor: 'rgb(75, 85, 99)',
                        rowHoverBg: '#f5f5f5',
                        headerSortHoverBg: '#fafafa',
                        headerSortActiveBg: '#fafafa',
                        borderColor: '#f0f0f0',
                        rowExpandedBg: '#fafafa',
                      },
                    },
                  }}
                >
                  <StyledTableContainer>
                    <AntTable
                      columns={columns}
                      dataSource={prepareChartData()}
                      rowKey="year"
                      onChange={handleTableChange}
                      pagination={{ 
                        pageSize: 10,
                        showSizeChanger: false,
                        showQuickJumper: true,
                        style: { marginTop: '16px' }
                      }}
                      scroll={{ x: 'max-content' }}
                    />
                  </StyledTableContainer>
                </ConfigProvider>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>
      </motion.div>
    </ThemeProvider>
  );
};

export default FeeCalculator; 